import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../scss/Product.scss';
import CallController from '../API/CallController';

interface ProductType {
  id: number;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  imageUrl: string;
  priceUnit: string;
}

type CampaignFilter = 'all' | 'discounted' | 'nonDiscounted';

const Product: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [products, setProducts] = useState<ProductType[]>([]);
  const [campaignFilter, setCampaignFilter] = useState<CampaignFilter>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');

  const maxProductPrice = products.length > 0
    ? Math.max(...products.map(p => p.discountedPrice ?? p.price))
    : 1000;

  useEffect(() => {
    if (!categoryId) return;

    fetch(CallController.GetProductsWithActiveCampaignsByCategoryAsync(Number(categoryId)))
      .then(async (res) => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Fel vid hämtning: ${res.status} - ${errorText}`);
        }
        return res.json();
      })
      .then((data) => {
        setProducts(data);
        setMaxPrice(maxProductPrice.toString());
      })
      .catch((error) => console.error(error));
  }, [categoryId, maxProductPrice]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinPrice(e.target.value);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxPrice(e.target.value);
  };

  const filteredProducts = products.filter(product => {
    const priceToCompare = product.discountedPrice ?? product.price;
    const min = minPrice === '' ? 0 : Number(minPrice);
    const max = maxPrice === '' ? Number.MAX_SAFE_INTEGER : Number(maxPrice);

    if (priceToCompare < min) return false;
    if (priceToCompare > max) return false;

    if (campaignFilter === 'discounted' && !(product.discountedPrice && product.discountedPrice < product.price)) return false;
    if (campaignFilter === 'nonDiscounted' && (product.discountedPrice && product.discountedPrice < product.price)) return false;

    return true;
  });

  return (
    <>
      <button
        className="filter-toggle-btn"
        onClick={() => setShowFilters(!showFilters)}
      >
        {showFilters ? 'Dölj filter' : 'Filtera'}
      </button>

      {showFilters && (
        <div className="filter-container">
          <div className="price-filter">
            <label>
              Prisintervall ({minPrice || 'min'} - {maxPrice || 'max'} {products[0]?.priceUnit ?? ''})
            </label>
            <div className="price-inputs">
              <input
                type="number"
                min={0}
                max={maxProductPrice}
                value={minPrice}
                onChange={handleMinChange}
                placeholder="Min pris"
              />
              <span>–</span>
              <input
                type="number"
                min={0}
                max={maxProductPrice}
                value={maxPrice}
                onChange={handleMaxChange}
                placeholder="Max pris"
              />
            </div>
          </div>

          <div className="campaign-filter">
            <label>
              <input
                type="radio"
                name="campaign"
                value="all"
                checked={campaignFilter === 'all'}
                onChange={() => setCampaignFilter('all')}
              />
              Alla produkter
            </label>
            <label>
              <input
                type="radio"
                name="campaign"
                value="discounted"
                checked={campaignFilter === 'discounted'}
                onChange={() => setCampaignFilter('discounted')}
              />
              Endast rabatterade
            </label>
            <label>
              <input
                type="radio"
                name="campaign"
                value="nonDiscounted"
                checked={campaignFilter === 'nonDiscounted'}
                onChange={() => setCampaignFilter('nonDiscounted')}
              />
              Ej rabatterade
            </label>
          </div>
        </div>
      )}

      <div className="product-banner">
        <img src="/pizza1.jpg" alt="Våra produkter" />
      </div>

      <div className="product-grid">
        {filteredProducts.length > 0 ? (
          filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <img src={`https://accordi-web-eqb2byhxd0feffhu.norwayeast-01.azurewebsites.net${product.imageUrl}`} alt={product.name} />
              <h4>{product.name}</h4>
              <p>
                {product.discountedPrice && product.discountedPrice < product.price ? (
                  <>
                    <span className="original-price">
                      {product.price} {product.priceUnit}
                    </span>
                    <span className="discounted-price">
                      {product.discountedPrice} {product.priceUnit}
                    </span>
                  </>
                ) : (
                  <span>{product.price} {product.priceUnit}</span>
                )}
              </p>
              <Link
                to={`/product/${product.id}`}
                state={{ categoryId }}
                className="read-more-btn"
              >
                Läs mer
              </Link>
            </div>
          ))
        ) : (
          <p>Inga produkter matchar filtreringen.</p>
        )}
      </div>
    </>
  );
};

export default Product;
